import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, ExternalLink, ArrowLeft, Sparkles } from "lucide-react";
import { templateList } from "../data/templates";

const ALL_INDUSTRIES = ["All", ...Array.from(new Set(templateList.map((t) => t.industry)))];

export default function ServicesGrid() {
  const [query, setQuery] = useState("");
  const [industry, setIndustry] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templateList.filter((t) => {
      if (industry !== "All" && t.industry !== industry) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        t.summary.toLowerCase().includes(q) ||
        t.key.toLowerCase().includes(q)
      );
    });
  }, [query, industry]);

  return (
    <div className="min-h-screen bg-[#05060F] text-white" data-testid="services-page">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl glass-strong border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-white" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <p className="hidden sm:block text-xs uppercase tracking-[0.24em] text-violet-300">
            Digital Onboarding · Demo Templates
          </p>
          <Link
            to="/"
            className="text-xs font-semibold px-4 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:opacity-90 transition-opacity text-white"
            data-testid="services-cta-home"
          >
            Get Started
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16 pb-12 lg:pt-24 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 -z-0">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[60%] h-[60%] rounded-full bg-violet-600/20 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[40%] h-[40%] rounded-full bg-fuchsia-500/10 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] uppercase tracking-[0.2em] font-semibold bg-violet-500/10 text-violet-300 border border-violet-500/20">
              <Sparkles className="w-3.5 h-3.5" /> 20 Ready-to-launch Templates
            </span>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
              Pick a template.{" "}
              <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-amber-200 bg-clip-text text-transparent">
                Launch in days.
              </span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-400 max-w-2xl">
              Ready-to-use website templates for Indian businesses. Preview any demo to see more.
            </p>
          </motion.div>

          {/* Search & filter */}
          <div className="mt-10 flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="relative flex-1 max-w-md" data-testid="services-search-wrapper">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search templates by name, industry…"
                data-testid="services-search"
                className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/60 transition-colors"
              />
            </div>

            <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" data-testid="services-filter">
              {ALL_INDUSTRIES.map((ind) => {
                const active = ind === industry;
                return (
                  <button
                    key={ind}
                    onClick={() => setIndustry(ind)}
                    data-testid={`industry-filter-${ind.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`whitespace-nowrap text-xs font-semibold px-4 py-2 rounded-full border transition-all ${
                      active
                        ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 border-transparent text-white"
                        : "bg-white/[0.04] border-white/10 text-slate-300 hover:border-violet-500/40"
                    }`}
                  >
                    {ind}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Cards Grid */}
      <section className="pb-24 lg:pb-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-16">No templates match your filters.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
              {filtered.map((t, i) => (
                <motion.article
                  key={t.key}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.06 }}
                  data-testid={`service-card-${t.key}`}
                  className="group relative rounded-3xl overflow-hidden bg-white/[0.04] border border-white/10 hover:border-violet-500/50 transition-all hover:-translate-y-1"
                >
                  <Link to={`/templates/${t.key}`} className="block">
                    <div className="aspect-[16/10] overflow-hidden relative">
                      <img
                        src={t.cover}
                        alt={t.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#05060F] via-transparent to-transparent opacity-70" />
                      {/* Category badge */}
                      <span
                        className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full backdrop-blur-md flex items-center gap-1.5"
                        style={{ background: "rgba(255,255,255,0.14)", color: "#fff", border: "1px solid rgba(255,255,255,0.22)" }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.theme?.primary || "#a78bfa" }} />
                        {t.industry}
                      </span>
                      {/* Module count pill */}
                      <span
                        className="absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full backdrop-blur-md inline-flex items-center gap-1"
                        style={{ background: "rgba(0,0,0,0.45)", color: "#fff", border: "1px solid rgba(255,255,255,0.18)" }}
                      >
                        <Sparkles className="w-3 h-3" /> {t.nav.length} sections
                      </span>
                      {/* Bottom name strip on image */}
                      <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
                        <p className="font-display text-xl text-white font-bold drop-shadow-lg leading-tight">
                          {t.brand?.name?.split(" ").slice(0, 3).join(" ") || t.name}
                        </p>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg font-bold leading-snug group-hover:text-violet-200 transition-colors">
                        {t.name}
                      </h3>
                      <p className="mt-2 text-sm text-slate-400 leading-relaxed line-clamp-2">{t.summary}</p>

                      {/* Module chips */}
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {t.nav.slice(0, 4).map((n) => (
                          <span key={n.slug} className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-white/[0.06] text-slate-300">
                            {n.label}
                          </span>
                        ))}
                        {t.nav.length > 4 && (
                          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-white/[0.06] text-slate-400">
                            +{t.nav.length - 4}
                          </span>
                        )}
                      </div>

                      <div className="mt-5 flex items-center justify-between gap-2 border-t border-white/5 pt-4">
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Sparkles className="w-3.5 h-3.5 text-violet-300" />
                          <span>Mobile-first &amp; responsive</span>
                        </div>
                        <span
                          data-testid={`view-demo-${t.key}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30 group-hover:shadow-violet-500/50 transition-shadow"
                        >
                          View Demo <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}

          {/* Footer band */}
          <div className="mt-20 rounded-3xl p-8 sm:p-10 bg-gradient-to-br from-violet-500/15 via-fuchsia-500/10 to-transparent border border-violet-500/20 text-center">
            <p className="text-xs uppercase tracking-[0.24em] text-violet-300 font-semibold mb-2">
              Don&apos;t see your industry?
            </p>
            <h3 className="font-display text-2xl sm:text-3xl font-bold">
              We build <span className="bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">custom</span> templates too.
            </h3>
            <p className="mt-3 text-sm text-slate-400 max-w-xl mx-auto">
              Our designers and engineers can tailor any of these or build from scratch for your unique brand.
            </p>
            <Link
              to="/"
              data-testid="services-cta-bottom"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold text-sm shadow-xl shadow-violet-500/30 hover:-translate-y-0.5 transition-transform"
            >
              Talk to our team <ExternalLink className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
